import { FormEvent, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { CardApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { Field, PageHeader, StatusBadge } from "../../components/common";
import { QrScanner } from "../../components/QrScanner";
import { DataTable } from "../../components/DataTable";
import { dateTime, peso } from "../../lib/format";

export default function CashierActivation() {
  const qc = useQueryClient();
  const [serial, setSerial] = useState("");
  const [card, setCard] = useState<any>(null);
  const [receipt, setReceipt] = useState("");
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: recent = [] } = useQuery({ queryKey: ["cashier", "recent"], queryFn: CardApi.recent });

  const lookup = async (s = serial) => {
    setErr(""); setCard(null);
    try { const c = await CardApi.lookup(s); setCard(c); setAmount(String(c.price / 100)); } catch (e) { setErr(errorMessage(e)); }
  };
  const pay = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const c = await CardApi.pay(card.serial, receipt, Math.round(Number(amount) * 100));
      setCard(c); setReceipt(""); toast.success(`Card ${c.serial} marked PAID`);
      qc.invalidateQueries({ queryKey: ["cashier"] });
    } catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Card activation" subtitle="Record the customer's payment and official receipt number. Only PAID cards can be registered." />
      <div className="fp-card space-y-3">
        <QrScanner testId="cashier-scanner" label="Scan card serial" onScan={(t) => { setSerial(t); lookup(t); }} />
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(); }}>
          <input className="fp-input font-mono uppercase" placeholder="Card serial number" value={serial} onChange={(e) => setSerial(e.target.value)} data-testid="cashier-serial-input" />
          <button className="fp-btn-primary h-12" data-testid="cashier-lookup-button"><Search size={16} /> Find</button>
        </form>
        {err && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="cashier-error">{err}</p>}
      </div>
      {card && (
        <div className="fp-card space-y-3" data-testid="cashier-card-details">
          <div className="flex items-center justify-between"><p className="font-mono text-lg font-bold">{card.serial}</p><StatusBadge status={card.status === "unpaid" ? "pending" : card.status === "paid" ? "paid" : "cancelled"} label={card.status} testId="cashier-card-status" /></div>
          <p className="text-sm">{card.package_name} · <strong>{peso(card.price)}</strong></p>
          {card.status === "unpaid" ? (
            <form onSubmit={pay} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <Field label="Official receipt no."><input className="fp-input uppercase" required minLength={3} value={receipt} onChange={(e) => setReceipt(e.target.value)} data-testid="cashier-receipt-input" /></Field>
              <Field label="Amount received (₱)"><input className="fp-input" type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cashier-amount-input" /></Field>
              <button className="fp-btn h-12 bg-[#FFC400] text-[#003F32]" disabled={busy} data-testid="cashier-mark-paid-button">Mark as PAID</button>
            </form>
          ) : (
            <p className="rounded-xl bg-slate-50 p-3 text-sm" data-testid="cashier-card-locked">
              {card.status === "paid" ? `Already paid · OR ${card.receipt_number} · ${dateTime(card.paid_at)}` : card.status === "registered" ? "Already registered to a customer — cannot be activated again." : card.status === "reserved" ? "Reserved by a customer — pending account verification." : "This card is void."}
            </p>
          )}
        </div>
      )}
      <DataTable testId="cashier-recent-table" rows={recent} empty="No paid cards yet" columns={[
        { key: "serial", label: "Serial", render: (c) => <span className="font-mono">{c.serial}</span> },
        { key: "pkg", label: "Package", render: (c) => c.package_name },
        { key: "or", label: "Receipt", render: (c) => c.receipt_number },
        { key: "amt", label: "Amount", render: (c) => peso(c.paid_amount) },
        { key: "by", label: "Cashier", render: (c) => c.paid_by_name },
        { key: "at", label: "Paid", render: (c) => dateTime(c.paid_at) },
        { key: "st", label: "Status", render: (c) => <StatusBadge status={c.status === "paid" ? "paid" : "active"} label={c.status} /> },
      ]} />
    </div>
  );
}
